import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Clock3,
  Plane,
  CheckCircle2,
  Loader2,
  XCircle,
  Layers,
  ListChecks,
  PhoneCall,
} from "lucide-react";
import { getStats, getProcesses, bulkPendingReminders, getTasks, completeTask } from "@/lib/portalApi";
import { StatusBadge } from "@/components/portal/StatusBadge";
import { formatDateTime } from "@/components/portal/portalConstants";
import { Button } from "@/components/ui/button";
import { BellRing } from "lucide-react";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, accent, testId, primary }) => (
  <div
    className={`rounded-2xl border p-5 ${
      primary ? "border-slate-700 bg-slate-900/70" : "border-slate-800 bg-slate-900/40"
    }`}
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <p className="text-sm text-slate-400">{label}</p>
      <span className={`h-9 w-9 grid place-items-center rounded-lg ${accent}`}>
        <Icon className="h-4 w-4" />
      </span>
    </div>
    <p className="mt-3 text-3xl font-bold">{value}</p>
  </div>
);

export const PortalDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reminding, setReminding] = useState(false);

  const markDone = async (id) => {
    try {
      await completeTask(id);
      setTasks((ts) => ts.filter((x) => x.id !== id));
      toast.success("Task completed");
    } catch (e) {
      toast.error("Could not update task");
    }
  };

  const sendReminders = async () => {
    setReminding(true);
    try {
      const res = await bulkPendingReminders();
      toast.success(`Reminders sent to ${res?.reminded ?? 0} customer(s)`);
    } catch (e) {
      toast.error("Could not send reminders");
    } finally {
      setReminding(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, procs, tks] = await Promise.all([getStats(), getProcesses(), getTasks().catch(() => [])]);
        setStats(s);
        setRecent(procs.slice(0, 6));
        setTasks(Array.isArray(tks) ? tks : []);
      } catch (e) {
        // handled globally
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="grid place-items-center py-20 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portal-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{t("portal.dashboard.title")}</h1>
          <p className="text-sm text-slate-400">{t("portal.dashboard.subtitle")}</p>
        </div>
        <Button
          onClick={sendReminders}
          disabled={reminding}
          className="rounded-full brand-gradient text-white hover:opacity-90"
          data-testid="bulk-reminders-button"
        >
          {reminding ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <BellRing className="h-4 w-4 mr-1.5" />}
          {t("portal.dashboard.remindPending", "Remind pending documents")}
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          primary
          icon={Clock3}
          label={t("portal.stats.pendingApprovals")}
          value={stats?.pending_approvals ?? 0}
          accent="bg-purple-500/15 text-purple-300"
          testId="stat-pending-approvals"
        />
        <StatCard
          primary
          icon={Plane}
          label={t("portal.stats.activeVisas")}
          value={stats?.active_visas ?? 0}
          accent="bg-cyan-500/15 text-cyan-300"
          testId="stat-active-visas"
        />
        <StatCard
          primary
          icon={CheckCircle2}
          label={t("portal.stats.completed")}
          value={stats?.completed ?? 0}
          accent="bg-venka-green/15 text-venka-green-light"
          testId="stat-completed"
        />
        <StatCard
          icon={Loader2}
          label={t("portal.stats.inProgress")}
          value={stats?.in_progress ?? 0}
          accent="bg-blue-500/15 text-blue-300"
          testId="stat-in-progress"
        />
        <StatCard
          icon={XCircle}
          label={t("portal.stats.rejected")}
          value={stats?.rejected ?? 0}
          accent="bg-red-500/15 text-red-300"
          testId="stat-rejected"
        />
        <StatCard
          icon={Layers}
          label={t("portal.stats.total")}
          value={stats?.total ?? 0}
          accent="bg-slate-500/15 text-slate-300"
          testId="stat-total"
        />
      </div>

      {tasks.length > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40" data-testid="tasks-queue">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
            <h2 className="font-semibold flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-venka-green-light" /> {t("portal.tasks.title", "Work queue · follow-ups")}
            </h2>
            <span className="text-xs text-slate-400">{tasks.length} {t("portal.tasks.open", "open")}</span>
          </div>
          <div className="divide-y divide-slate-800">
            {tasks.map((tk) => {
              const due = tk.due_at ? new Date(tk.due_at) : null;
              const now = new Date();
              const overdue = due && due < now;
              const soon = due && !overdue && due - now < 24 * 3600 * 1000;
              return (
                <div key={tk.id} className="flex items-center justify-between gap-4 px-5 py-3" data-testid={`task-${tk.id}`}>
                  <button
                    onClick={() => tk.process_id && navigate(`/portal/processes/${tk.process_id}`)}
                    className="min-w-0 text-left"
                  >
                    <p className="text-sm font-medium truncate flex items-center gap-2">
                      <PhoneCall className="h-3.5 w-3.5 text-venka-green-light shrink-0" /> {tk.title}
                    </p>
                    <p className="text-xs text-slate-400 truncate">
                      {tk.customer_name || "—"}
                      {tk.destination ? ` · ${tk.destination}` : ""}
                      {tk.reference ? ` · ${tk.reference}` : ""}
                    </p>
                  </button>
                  <div className="flex items-center gap-3 shrink-0">
                    <span
                      className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 ${
                        overdue
                          ? "bg-red-500/15 text-red-300 border-red-500/30"
                          : soon
                          ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
                          : "bg-slate-700/40 text-slate-300 border-slate-600"
                      }`}
                      data-testid={`task-due-${tk.id}`}
                    >
                      {overdue ? t("portal.tasks.overdue", "Overdue") : soon ? t("portal.tasks.soon", "Due soon") : t("portal.tasks.due", "Due")}
                      {due ? ` · ${due.toLocaleDateString(i18n.resolvedLanguage)}` : ""}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-slate-700 bg-slate-900 hover:bg-slate-800"
                      onClick={() => markDone(tk.id)}
                      data-testid={`task-done-${tk.id}`}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> {t("portal.tasks.done", "Done")}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-800 bg-slate-900/40">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <h2 className="font-semibold">{t("portal.dashboard.recentTitle")}</h2>
          <button
            className="text-xs text-venka-green-light hover:underline"
            onClick={() => navigate("/portal/processes")}
            data-testid="dashboard-view-all"
          >
            {t("portal.dashboard.viewAll")}
          </button>
        </div>
        <div className="divide-y divide-slate-800">
          {recent.length === 0 && (
            <p className="px-5 py-6 text-sm text-slate-400">{t("portal.table.empty")}</p>
          )}
          {recent.map((p) => (
            <button
              key={p.id}
              onClick={() => navigate(`/portal/processes/${p.id}`)}
              className="w-full flex items-center justify-between gap-4 px-5 py-3 text-left hover:bg-slate-800/40 transition-colors"
              data-testid={`dashboard-recent-${p.id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">
                  {p.customer_name || "—"} · {p.process_type_name}
                </p>
                <p className="text-xs text-slate-400">
                  {p.country_destination || "—"} · {formatDateTime(p.updated_at, i18n.resolvedLanguage)}
                </p>
              </div>
              <StatusBadge status={p.status} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PortalDashboard;
